<template>
  <!-- Оверлей звонка Telegram (DEV-924): TG-C01 (исходящий), TG-C02 (активный),
       TG-C04 (входящий). Завершённый звонок уходит в пост-колл карточку (DEV-925),
       поэтому оверлей его не показывает. DEV-2684: смонтирован в DashboardLayout и
       висит в углу экрана на любой странице CRM, а не только во вкладке чата. -->
  <Transition name="tg-call-fade">
    <div
      v-if="visible"
      class="tg-call"
      :class="{ 'tg-call--incoming': callState === 'incoming' }"
      role="dialog"
      :aria-label="statusLabel"
    >
      <!-- TG-C03: короткая карточка «Звонок завершён» (~1.5с) после нормального завершения -->
      <div v-if="showEnded" class="tg-call__ended" role="status">
        <span class="tg-call__ended-check" aria-hidden="true"><CheckIcon :size="20" /></span>
        <span class="tg-call__ended-text">
          <span class="tg-call__ended-title">{{ $t('messenger.telegram.call.endedTitle') }}</span>
          <span class="tg-call__ended-sub"
            >{{ $t('messenger.callEvent.duration') }} · {{ formattedDuration }}</span
          >
        </span>
      </div>

      <div v-else class="tg-call__card">
        <div class="tg-call__avatar" aria-hidden="true">{{ initials }}</div>
        <div class="tg-call__name">{{ displayName }}</div>

        <div class="tg-call__status">
          <span
            v-if="callState === 'active'"
            class="tg-call__timer"
            aria-live="polite"
            aria-atomic="true"
          >
            {{ formattedDuration }}
          </span>
          <span v-else class="tg-call__status-text">{{ statusLabel }}</span>
        </div>

        <!-- Входящий: отклонить / принять -->
        <div v-if="callState === 'incoming'" class="tg-call__actions">
          <div class="tg-call__action">
            <button
              type="button"
              class="tg-call__btn tg-call__btn--decline"
              :title="$t('messenger.telegram.call.decline')"
              @click="declineIncoming"
            >
              <XIcon :size="24" />
            </button>
            <span class="tg-call__btn-label">{{ $t('messenger.telegram.call.decline') }}</span>
          </div>
          <div class="tg-call__action">
            <button
              type="button"
              class="tg-call__btn tg-call__btn--accept"
              :title="$t('messenger.telegram.call.accept')"
              @click="acceptIncoming"
            >
              <PhoneIcon :size="24" />
            </button>
            <span class="tg-call__btn-label">{{ $t('messenger.telegram.call.accept') }}</span>
          </div>
        </div>

        <!-- Активный: без звука / завершить -->
        <div v-else-if="callState === 'active'" class="tg-call__actions">
          <div class="tg-call__action">
            <button
              type="button"
              class="tg-call__btn tg-call__btn--mute"
              :class="{ 'tg-call__btn--mute-on': isMuted }"
              :title="$t('messenger.telegram.call.mute')"
              :aria-pressed="isMuted"
              @click="toggleMute"
            >
              <MicOffIcon v-if="isMuted" :size="24" />
              <MicIcon v-else :size="24" />
            </button>
            <span class="tg-call__btn-label">{{ $t('messenger.telegram.call.mute') }}</span>
          </div>
          <div class="tg-call__action">
            <button
              type="button"
              class="tg-call__btn tg-call__btn--hangup"
              :title="$t('messenger.telegram.call.hangup')"
              @click="hangup"
            >
              <PhoneOffIcon :size="24" />
            </button>
            <span class="tg-call__btn-label">{{ $t('messenger.telegram.call.hangup') }}</span>
          </div>
        </div>

        <!-- Исходящий (initiating/connecting/ringing): отменить -->
        <div v-else class="tg-call__actions">
          <div class="tg-call__action">
            <button
              type="button"
              class="tg-call__btn tg-call__btn--hangup"
              :title="$t('messenger.telegram.call.cancel')"
              @click="hangup"
            >
              <XIcon :size="24" />
            </button>
            <span class="tg-call__btn-label">{{ $t('messenger.telegram.call.cancel') }}</span>
          </div>
        </div>

        <!-- Без разрешения системного уведомления звонок в фоне не увидят (как у WABA). -->
        <button
          v-if="callState === 'incoming' && permission === 'default'"
          type="button"
          class="tg-call__notify"
          @click="askPermission"
        >
          {{ $t('messenger.callEvent.enableNotifications') }}
        </button>

        <p v-if="errorLabel" class="tg-call__error">{{ errorLabel }}</p>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { PhoneIcon, PhoneOffIcon, MicIcon, MicOffIcon, XIcon, CheckIcon } from 'lucide-vue-next'
import { useTelegramCall } from '@/features/messenger/composables/useTelegramCall'
import { useIncomingCallAlert } from '@/features/messenger/composables/useIncomingCallAlert'

const { t } = useI18n()

const {
  callState,
  callId,
  peerName,
  isMuted,
  isActive,
  callError,
  formattedDuration,
  acceptIncoming,
  declineIncoming,
  hangup,
  toggleMute,
  setSyncEnabled,
  syncRinging,
} = useTelegramCall()

// DEV-2684. Окно смонтировано только у тех, кому звонки Telegram возможны: эта вкладка
// спрашивает звонящие при подключении сокета — и сразу, если сокет уже открыт.
onMounted(() => {
  setSyncEnabled(true)
  syncRinging()
})

// TG-C03: нормально завершённый разговор гаснет через короткое «Звонок завершён».
const showEnded = ref(false)
let endedTimer: ReturnType<typeof setTimeout> | null = null

watch(callState, (next, prev) => {
  if (next === 'ended' && prev === 'active' && !callError.value) {
    showEnded.value = true
    if (endedTimer) clearTimeout(endedTimer)
    endedTimer = setTimeout(() => {
      showEnded.value = false
    }, 1500)
  }
})

onUnmounted(() => {
  if (endedTimer) clearTimeout(endedTimer)
  setSyncEnabled(false)
})

// Оверлей виден на живых стадиях звонка. Завершённый → короткое TG-C03,
// затем пост-колл карточка (DEV-925).
const visible = computed(() => isActive.value || callState.value === 'incoming' || showEnded.value)

const displayName = computed(() => peerName.value || t('messenger.telegram.call.unknownPeer'))

// DEV-2684. Звонящий слышен и виден вне вкладки: мелодия (звонит одна вкладка),
// системное уведомление и заголовок вкладки — как у звонка на общий номер WhatsApp.
const { permission, askPermission } = useIncomingCallAlert(
  computed(() =>
    callState.value === 'incoming' && callId.value
      ? {
          id: callId.value,
          title: t('messenger.telegram.call.incomingTitle'),
          body: displayName.value,
        }
      : null,
  ),
)

const initials = computed(() =>
  displayName.value
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join(''),
)

const statusLabel = computed(() => {
  switch (callState.value) {
    case 'initiating':
    case 'connecting':
      return t('messenger.telegram.call.state.connecting')
    case 'ringing':
      return t('messenger.telegram.call.state.ringing')
    case 'active':
      return t('messenger.telegram.call.state.active')
    case 'incoming':
      return t('messenger.telegram.call.state.incoming')
    default:
      return ''
  }
})

const errorLabel = computed(() => {
  if (!callError.value) return ''
  const key = `messenger.telegram.call.error.${callError.value}`
  const label = t(key)
  return label === key ? t('messenger.telegram.call.error.generic') : label
})
</script>

<style scoped>
/* DEV-2684. Сверху справа на любой странице, как окно звонка WhatsApp (WabaCallOverlay):
   внизу справа живёт виджет SIP-звонка. Страницу не затемняет — в разговоре CRM
   остаётся рабочей. Выше модалки и поповеров: на звонок отвечают из любого окна. */
.tg-call {
  position: fixed;
  top: 76px;
  right: var(--spacing-xl, 24px);
  z-index: calc(var(--z-index-popover, 10000) + 1);
}

/* Пока звонят — выше и тостов: на узком экране они сверху и ложились бы на «Принять». */
.tg-call--incoming {
  z-index: calc(var(--z-index-toast, 10010) + 1);
}

.tg-call__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 240px;
  max-width: calc(100vw - 2 * var(--spacing-lg, 16px));
  padding: 28px 24px;
  background: var(--color-background-primary, #fff);
  border-radius: 20px;
  box-shadow:
    0 12px 40px rgba(15, 33, 51, 0.22),
    0 2px 8px rgba(15, 33, 51, 0.1);
}

.tg-call__avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2aabee, #229ed9);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 4px;
}

.tg-call__name {
  max-width: 100%;
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary, #18181b);
  text-align: center;
  line-height: 1.2;
  overflow-wrap: break-word;
  /* длинная фамилия на телефоне переносится по слогам, а не посреди слова */
  hyphens: auto;
}

.tg-call__status {
  min-height: 20px;
}

.tg-call__status-text {
  font-size: 14px;
  color: #229ed9;
  font-weight: 500;
}

.tg-call__timer {
  font-size: 16px;
  color: #229ed9;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.tg-call__actions {
  display: flex;
  gap: 28px;
  margin-top: 12px;
}

.tg-call__action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.tg-call__btn {
  width: 60px;
  height: 60px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  transition:
    transform 0.12s ease,
    filter 0.12s ease;
}

.tg-call__btn:hover {
  filter: brightness(1.06);
}

.tg-call__btn:active {
  transform: scale(0.94);
}

.tg-call__btn:focus-visible {
  outline: 3px solid rgba(34, 158, 217, 0.4);
  outline-offset: 2px;
}

.tg-call__btn--hangup,
.tg-call__btn--decline {
  background: #ef4444;
}

.tg-call__btn--accept {
  background: #22c55e;
}

.tg-call__btn--mute {
  background: #6b7280;
}

.tg-call__btn--mute-on {
  background: #f59e0b;
  color: #18181b;
}

.tg-call__btn-label {
  font-size: 12px;
  color: var(--color-text-secondary, #52525b);
}

.tg-call__notify {
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 12px;
  color: var(--color-primary, #4f46e5);
  text-align: center;
  cursor: pointer;
}

.tg-call__notify:hover {
  text-decoration: underline;
}

.tg-call__error {
  margin: 8px 0 0;
  font-size: 12px;
  color: #ef4444;
  text-align: center;
}

/* TG-C03 — короткая карточка завершения */
.tg-call__ended {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  background: var(--color-background-primary, #fff);
  border-radius: 16px;
  box-shadow:
    0 12px 40px rgba(15, 33, 51, 0.22),
    0 2px 8px rgba(15, 33, 51, 0.1);
}

.tg-call__ended-check {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #22c55e;
  color: #fff;
  flex-shrink: 0;
}

.tg-call__ended-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tg-call__ended-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary, #18181b);
}

.tg-call__ended-sub {
  font-size: 12px;
  color: var(--color-text-secondary, #667781);
  font-variant-numeric: tabular-nums;
}

/* Появление/исчезновение */
.tg-call-fade-enter-active,
.tg-call-fade-leave-active {
  transition: opacity 0.18s ease;
}

.tg-call-fade-enter-from,
.tg-call-fade-leave-to {
  opacity: 0;
}

@media (max-width: 600px) {
  .tg-call {
    top: 68px;
    right: var(--spacing-lg, 16px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .tg-call-fade-enter-active,
  .tg-call-fade-leave-active {
    transition: none;
  }
  .tg-call__btn:active {
    transform: none;
  }
}
</style>
